import React from "react";
import { FiPlus, FiSearch } from "react-icons/fi";
import { AgentPresenceToggle } from "./AgentPresenceToggle";
import { formatDurationShort } from "./supportUtils";

/**
 * One counter in the ops bar. `tone="danger"` is the red zone — the only
 * number here that is ever an alarm.
 *
 * @param {{ label: string, value: React.ReactNode, tone?: 'default'|'danger' }} props
 */
const Counter = ({ label, value, tone = "default" }) => (
  <span
    className={`whitespace-nowrap ${
      tone === "danger" ? "text-error-red-light" : "text-gray-400"
    }`}
  >
    {label}{" "}
    <span className={`font-bold ${tone === "danger" ? "" : "text-white"}`}>{value}</span>
  </span>
);

/**
 * The support workspace's own top bar — a dark strip above the four panes
 * carrying the desk's vital signs, search, the agent's availability, and the
 * one create action.
 *
 * The counters come from `views/counts` and `metrics`; both are already
 * fetched for the views rail and the queue, so this renders whatever has
 * landed and shows a dash for the rest rather than blocking on either.
 *
 * @param {Object} props
 * @param {{ views?: Object, departments?: Object }} props.counts
 * @param {Object|null} props.metrics - `GET /support/metrics` (staff only).
 * @param {string} props.search
 * @param {(value: string) => void} props.onSearchChange
 * @param {() => void} props.onNewTicket
 *
 * The design puts a notification bell here; FlexBoard's app sidebar already
 * owns one globally, so this bar deliberately has none — two bells competing
 * for the same unread count is worse than the mock's single one.
 */
export function SupportOpsBar({ counts, metrics, search, onSearchChange, onNewTicket }) {
  const views = counts?.views || {};
  const dash = "—";

  return (
    <header className="flex min-h-[52px] flex-shrink-0 flex-wrap items-center gap-3 bg-gray-900 px-3.5 py-2 lg:flex-nowrap lg:gap-3.5">
      <h1 className="m-0 text-sm font-bold tracking-wide text-white">SUPPORT</h1>

      <div className="hidden items-center gap-3.5 font-mono text-[11px] md:flex">
        <Counter label="OPEN" value={views.all_open ?? metrics?.open_tickets ?? dash} />
        <Counter label="UNASSIGNED" value={views.pool ?? dash} />
        <Counter
          label="RED ZONE"
          tone="danger"
          value={views.red_zone ?? metrics?.breached_tickets ?? dash}
        />
        <Counter
          label="MED 1ST REPLY"
          value={
            metrics?.median_first_response_seconds == null
              ? dash
              : formatDurationShort(metrics.median_first_response_seconds)
          }
        />
        <Counter
          label="CSAT"
          value={metrics?.csat_avg == null ? dash : Number(metrics.csat_avg).toFixed(1)}
        />
      </div>

      <label className="flex h-7 min-w-0 flex-1 items-center gap-2 rounded-md bg-white/10 px-2.5 lg:max-w-[300px] lg:flex-none">
        <FiSearch aria-hidden="true" size={13} className="shrink-0 text-gray-400" />
        <span className="sr-only">Search tickets</span>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search · #id, client, tag"
          className="min-w-0 flex-1 bg-transparent text-xs text-white placeholder:text-gray-400 focus:outline-none"
        />
      </label>

      <div className="ml-auto flex items-center gap-2">
        <AgentPresenceToggle variant="dark" />
        <button
          type="button"
          onClick={onNewTicket}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-orange-primary px-3 text-xs font-semibold text-white transition-colors hover:brightness-95"
        >
          <FiPlus aria-hidden="true" size={13} />
          <span className="hidden sm:inline">New ticket</span>
        </button>
      </div>
    </header>
  );
}

export default SupportOpsBar;
